import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, MaskLines } from "@/components/site/Reveal";
import { Connect } from "@/components/site/Connect";
import { Seo } from "@/components/site/Seo";

const people = [
  { slug: "apoorva-chandra", name: "Dr Apoorva Chandra", role: "Founder - The Desi Science Page", img: "/img/apoorva.jpg" },
  { slug: "vidya-sri", name: "Dr Vidya Sri", role: "Co-founder & Chief Reality Check Officer, The Desi Science Page", img: "/img/vidya.jpg", initials: "VS" },
];

export const Monogram = ({ initials, className = "" }) => (
  <div className={`w-full h-full flex items-center justify-center bg-panel grid-lines-soft ${className}`}>
    <span className="font-display font-bold text-6xl text-lime/90 tracking-tight">{initials}</span>
  </div>
);

const PersonCard = ({ p, i }) => (
  <motion.article
    data-testid={`person-card-${p.slug}`}
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className="group w-full max-w-[300px]"
  >
    <Link to={`/people/${p.slug}`} className="block" aria-label={`Read about ${p.name}`}>
      <div className="relative w-36 sm:w-44 aspect-[4/5] rounded-2xl overflow-hidden border border-white/15 shadow-[0_20px_50px_-20px_rgba(200,244,90,0.35)]">
        {p.img ? (
          <img src={p.img} alt={p.name} className="w-full h-full object-cover object-top grayscale contrast-125 group-hover:grayscale-0 group-hover:contrast-100 group-hover:scale-105 transition-[filter,transform] duration-700" />
        ) : (
          <Monogram initials={p.initials} className="transition-transform duration-700 group-hover:scale-105" />
        )}
      </div>
    </Link>
    <Link data-testid={`person-name-${p.slug}`} to={`/people/${p.slug}`} className="block mt-6">
      <h2 className="font-display font-bold text-2xl text-white group-hover:text-lime transition-colors duration-300">{p.name}</h2>
    </Link>
    <p data-testid={`person-role-${p.slug}`} className="mt-1 font-mono text-[12px] uppercase tracking-[0.12em] text-zinc-400">{p.role}</p>
    <Link
      data-testid={`person-learn-more-${p.slug}`}
      to={`/people/${p.slug}`}
      className="mt-5 inline-flex items-center gap-2 rounded-full border border-lime/50 px-5 py-2.5 font-mono text-[12px] uppercase tracking-[0.2em] text-lime hover:bg-lime hover:text-ink transition-colors duration-300"
    >
      Learn more <ArrowUpRight className="w-4 h-4" />
    </Link>
  </motion.article>
);

export default function PeopleIndex() {
  return (
    <main data-testid="people-index-page">
      <Seo
        title="The People Behind | The Desi Science Page"
        description="Meet the people behind The Desi Science Page: founder Dr Apoorva Chandra and co-founder Dr Vidya Sri."
        path="/people"
      />
      <section className="relative px-5 sm:px-10 lg:px-16 pt-36 pb-24 overflow-hidden min-h-[80svh]">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -right-40 top-10 w-[520px] h-[520px] rounded-full bg-lime/10 blur-[120px]" />
        <div className="relative">
          <Eyebrow className="mb-6">// Who we are</Eyebrow>
          <h1 className="font-display font-bold uppercase leading-[0.88] text-5xl sm:text-7xl lg:text-8xl mb-16">
            <MaskLines onLoad lines={["The people", <span className="outline-text">behind this page</span>]} />
          </h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {people.map((p, i) => <PersonCard key={p.slug} p={p} i={i} />)}
          </div>
        </div>
      </section>
      <Connect />
    </main>
  );
}
